import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SEOProps {
    title?: string;
    description?: string;
    canonical?: string;
    ogImage?: string;
    ogType?: string;
    noindex?: boolean;
    jsonLd?: Record<string, any> | Record<string, any>[];
}

const SEO = ({ title, description, canonical, ogImage, ogType, noindex, jsonLd }: SEOProps) => {
    const { i18n } = useTranslation();
    const baseTitle = "CitoyenPro";
    const defaultDesc = "Préparez votre examen de citoyenneté canadienne avec CitoyenPro. Quiz adaptatifs, flashcards interactives, résumés audio et examens blancs chronométrés. +2400 questions à jour.";
    const defaultDescEn = "Prepare for your Canadian citizenship exam with CitoyenPro. Adaptive quizzes, interactive flashcards, audio summaries and timed mock exams. +2400 up-to-date questions.";
    const baseUrl = "https://citoyenpro.nnt-quebec.com";
    const defaultImage = `${baseUrl}/logo/og-image.png`;

    const currentLang = i18n.language?.startsWith('en') ? 'en' : 'fr';
    const alternateLang = currentLang === 'fr' ? 'en' : 'fr';

    useEffect(() => {
        // Title
        const finalTitle = title ? `${title} | ${baseTitle}` : `${baseTitle} - Préparation Citoyenneté Canadienne`;
        document.title = finalTitle;

        // HTML lang attribute
        document.documentElement.lang = currentLang;

        // Meta Description
        const descContent = description || (currentLang === 'fr' ? defaultDesc : defaultDescEn);
        setMeta('name', 'description', descContent);

        // Robots
        if (noindex) {
            setMeta('name', 'robots', 'noindex, nofollow');
        } else {
            removeMeta('name', 'robots');
        }

        // Canonical
        const canonicalUrl = canonical ? `${baseUrl}${canonical}` : baseUrl;
        setLink('canonical', canonicalUrl);

        // Hreflang alternate links
        setLink('alternate', `${baseUrl}${canonical || '/'}`, currentLang);
        setLink('alternate', `${baseUrl}${canonical || '/'}`, alternateLang);
        setLink('alternate', `${baseUrl}${canonical || '/'}`, 'x-default');

        // Open Graph
        setMeta('property', 'og:title', finalTitle);
        setMeta('property', 'og:description', descContent);
        setMeta('property', 'og:url', canonicalUrl);
        setMeta('property', 'og:image', ogImage || defaultImage);
        setMeta('property', 'og:type', ogType || 'website');
        setMeta('property', 'og:site_name', 'CitoyenPro');
        setMeta('property', 'og:locale', currentLang === 'fr' ? 'fr_CA' : 'en_CA');
        setMeta('property', 'og:locale:alternate', currentLang === 'fr' ? 'en_CA' : 'fr_CA');

        // Twitter Card
        setMeta('property', 'twitter:card', 'summary_large_image');
        setMeta('property', 'twitter:title', finalTitle);
        setMeta('property', 'twitter:description', descContent);
        setMeta('property', 'twitter:image', ogImage || defaultImage);
        setMeta('property', 'twitter:url', canonicalUrl);

        // JSON-LD Structured Data
        removeJsonLd();
        if (jsonLd) {
            const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            schemas.forEach((schema, index) => {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.id = `seo-jsonld-${index}`;
                script.textContent = JSON.stringify(schema);
                document.head.appendChild(script);
            });
        }

        return () => {
            removeJsonLd();
        };
    }, [title, description, canonical, ogImage, ogType, noindex, jsonLd, currentLang]);

    return null;
};

// Helper: set or create <meta> tag
function setMeta(attr: 'name' | 'property', key: string, content: string) {
    let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
    if (el) {
        el.setAttribute('content', content);
    } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute('content', content);
        document.head.appendChild(el);
    }
}

// Helper: remove <meta> tag
function removeMeta(attr: 'name' | 'property', key: string) {
    const el = document.querySelector(`meta[${attr}="${key}"]`);
    if (el) el.remove();
}

// Helper: set or create <link> tag
function setLink(rel: string, href: string, hreflang?: string) {
    const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
    let el = document.querySelector(selector) as HTMLLinkElement | null;
    if (el) {
        el.setAttribute('href', href);
    } else {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute('href', href);
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
    }
}

// Helper: remove all JSON-LD scripts
function removeJsonLd() {
    document.querySelectorAll('script[id^="seo-jsonld-"]').forEach(el => el.remove());
}

export default SEO;
